import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { endpoints, type Subscription, type Tier } from '../api/endpoints';
import { ApiException } from '../api/client';
import { Skeleton, ErrorState } from '../components/states';
import { isTelegram } from '../telegram/detect';

type PaidTier = 'pro' | 'family';

interface TierCard {
  id: Tier;
  name: string;
  price: string;
}
const TIERS: TierCard[] = [
  { id: 'free', name: 'Free', price: '0 ₽' },
  { id: 'pro', name: 'Pro', price: '299 ₽/мес' },
  { id: 'family', name: 'Family', price: '999 ₽/мес' },
];

interface FeatureRow {
  label: string;
  free: string;
  pro: string;
  family: string;
}
const FEATURES: FeatureRow[] = [
  { label: 'Рекомендации и Холодильник', free: 'да', pro: 'да', family: 'да' },
  { label: 'Импорт рецептов по ссылке', free: 'да', pro: 'да', family: 'да' },
  { label: 'Рецептов в коллекции', free: 'до 5', pro: 'без лимита', family: 'без лимита' },
  { label: 'Профили семьи', free: '1', pro: '1', family: 'до 8' },
  { label: 'Адаптация под диету', free: '—', pro: '—', family: 'да' },
];

function defaultRedirect(url: string) {
  // Внутри Telegram внешние ссылки открываем через WebApp API (обычный assign там не работает корректно).
  if (isTelegram() && window.Telegram?.WebApp) {
    window.Telegram.WebApp.openLink(url);
  } else {
    window.location.assign(url);
  }
}

export function BillingScreen({
  onCheckoutRedirect = defaultRedirect,
}: { onCheckoutRedirect?: (url: string) => void } = {}) {
  const navigate = useNavigate();
  const [sub, setSub] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<PaidTier | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSub(await endpoints.getSubscription());
    } catch (e) {
      setError(e instanceof ApiException ? e.body.message : 'Не удалось загрузить тариф');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Намеренный fetch-in-effect (план без TanStack): разовый setState на маунте — приемлемо.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  // Self-heal: на маунте фоном примиряем незавершённые платежи (оплата из Telegram
  // возвращается во внешний браузер — серверу нужен явный повторный «доактивируй»).
  // Не блокирует показ карточек (load() уже отрисовал тарифы); ошибки игнорируем.
  useEffect(() => {
    let cancelled = false;
    void endpoints
      .claimPending()
      .then((s) => {
        if (cancelled) return;
        setSub(s);
        if (s.active && s.tier !== 'free') localStorage.removeItem('recipt-billing-pending');
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  async function buy(tier: PaidTier) {
    setSubmitting(tier);
    setCheckoutError(null);
    try {
      const { paymentId, confirmationUrl } = await endpoints.checkout(tier);
      localStorage.setItem('recipt-billing-pending', JSON.stringify({ paymentId, tier }));
      onCheckoutRedirect(confirmationUrl);
    } catch (e) {
      if (e instanceof ApiException && e.status === 503) {
        setCheckoutError('Оплата временно недоступна, попробуйте позже');
      } else {
        setCheckoutError(e instanceof ApiException ? e.body.message : 'Не удалось начать оплату');
      }
      setSubmitting(null);
    }
  }

  function featureValue(row: FeatureRow, tier: Tier): string {
    return tier === 'free' ? row.free : tier === 'pro' ? row.pro : row.family;
  }

  // Прячем тарифы ниже текущего: показываем текущий и те, что выше (TIERS отсортирован по возрастанию).
  const currentIndex = sub ? TIERS.findIndex((t) => t.id === sub.tier) : -1;
  const visibleTiers = sub ? TIERS.slice(currentIndex) : TIERS;
  const onTopTier = sub != null && currentIndex === TIERS.length - 1;

  return (
    <div className="mx-auto min-h-screen max-w-md bg-cream px-4 pb-10 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate(-1)}
          aria-label="Назад"
          className="rounded-full bg-white/90 px-3 py-1.5 text-ink shadow"
        >
          ←
        </button>
        <h1 className="text-xl font-bold text-ink">Тарифы</h1>
      </div>

      {loading && (
        <div className="mt-6 space-y-3">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      )}

      {!loading && error && (
        <div className="mt-6">
          <ErrorState message={error} onRetry={() => void load()} />
        </div>
      )}

      {!loading && !error && sub && (
        <div className="mt-5 space-y-4">
          {visibleTiers.map((t) => {
            const current = sub.tier === t.id;
            return (
              <div
                key={t.id}
                className={`rounded-2xl bg-white p-4 shadow-card ${current ? 'ring-2 ring-primary-600' : ''}`}
              >
                <div className="flex items-baseline justify-between">
                  <h2 className="text-lg font-bold text-ink">{t.name}</h2>
                  <span className="text-sm font-semibold text-ink/70">{t.price}</span>
                </div>
                {current && (
                  <p className="mt-1 inline-block rounded-full bg-primary-50 px-2 py-0.5 text-xs font-semibold text-primary-700">
                    Ваш тариф
                  </p>
                )}
                <ul className="mt-3 space-y-1">
                  {FEATURES.map((row) => (
                    <li key={row.label} className="flex justify-between text-sm">
                      <span className="text-ink/70">{row.label}</span>
                      <span className="font-medium text-ink">{featureValue(row, t.id)}</span>
                    </li>
                  ))}
                </ul>
                {t.id !== 'free' && !current && (
                  <button
                    onClick={() => void buy(t.id as PaidTier)}
                    disabled={submitting !== null}
                    className="mt-4 w-full rounded-xl bg-primary-600 px-4 py-3 font-semibold text-white shadow-card transition active:scale-[0.98] disabled:opacity-50"
                  >
                    {submitting === t.id ? 'Переходим к оплате…' : `Оформить ${t.name}`}
                  </button>
                )}
              </div>
            );
          })}
          {onTopTier && (
            <p className="rounded-xl bg-primary-50 px-4 py-3 text-center text-sm font-medium text-primary-700">
              Это максимальный тариф — у вас открыты все возможности.
            </p>
          )}
          {checkoutError && (
            <p role="alert" className="rounded-xl bg-primary-50 px-4 py-3 text-sm text-primary-700 ring-1 ring-inset ring-primary-100">
              {checkoutError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
